import { useEffect, useMemo, useState } from "react";
import { connect, useDispatch } from "react-redux";
import Grid from "../../../../components/Grid";
import CallToAction from "../../components/CallToAction";
import { Group } from "../../../../types";
import LinkUi from "../../../../components/Link";
import { Link as RrdLink } from "react-router-dom";
import Dropdown from "../../../../components/DropdownBis";
import videoRecorder from "../../../../assets/icons/video-recorder.svg";
import sessionImage from "../../../../assets/icons/video-camera.svg";
import SessionCard from "../components/SessionCard";
import ReducedSessionCard from "../components/ReducedSessionCard";
import { getGroupById } from "../../../../store/actions/group";
import ModalShareLink from "../../components/ModalShareLink";
import { SectionTitle } from "../../components/Statistics/styles";
import { Name } from "../../../../pages/Dashboard/Posts/List/components/CategoryListHeader/styles";
import { motion } from "framer-motion";
import { isBeforeXDays, sessionExpired, sessionExpiredWithMargin, convertFloatHourToMinutes } from "../../../../helpers/dates-hanlder";
import Button from "../../../../components/Button";
import { showModal } from "../../../../store/actions/modal";

import {
  CardsGrid,
  CardsGridLarge,
  SessionCardSkeleton,
  DeleteButton,
  Text
} from "./styles";
import {
  getAllSessions,
  resetGetOneSession,
  resetDeleteBulkSession,
} from "../../../../store/actions/session";
import { SessionInDto } from "../../../../types/session.dto";
import EmptyCard from "../../components/EmptyCard";
import { useLayout } from "../../../../providers/LayoutProvider";

import BulkDeleteSessionsModal from "../components/BulkDeleteSessionsModal";
import { Icon } from "../../../../components/Icon/styles";
import { useTheme } from "styled-components";
import trashIcon from "../../../../assets/icons/trash.svg";
import DeleteSessionModal from "../components/DeleteSessionModal";
import SingleDeleteSessionModal from "../components/SingleDeleteSessionModal";
import FloatingMenu from "../components/FloatingMenu";
import { useWindowSize } from "../../../../hooks/useWindowSize";

interface ComponentProps {
  group: Group;
  groupStates: {
    loading: boolean;
    success: boolean;
    error: boolean | string;
  };
  sessions: SessionInDto[];
  sessionsStates: {
    loading: boolean;
    success: boolean;
    error: boolean | string;
  };
  match;
  children?;
}

const Component = ({
  group,
  groupStates,
  sessions,
  sessionsStates,
  match,
  children,
}: ComponentProps) => {
  const { isMobile } = useWindowSize();
  const theme = useTheme() as any;
  const { layout, setLayout } = useLayout();
  const [futureSessionsCardSize, setFutureSessionsCardSize] = useState<string | null>("large")
  const [selectedSessions, setSelectedSessions] = useState<number[]>([]);
  const [sessionToDelete, setSessionToDelete] = useState<SessionInDto | null>(null);

  const dispatch = useDispatch();

  useEffect(() => {
    if (!!match.params) {
      dispatch(resetGetOneSession());
      dispatch(getAllSessions({ idGroup: match.params.idGroup }));

      if (group?.id !== Number(match?.params?.idGroup)) {
        dispatch(getGroupById({ group_id: match.params.idGroup }));
      }
    }
  }, [match]);

  useEffect(() => {
    if (sessionsStates.success && sessions?.filter((session) => !sessionExpired(session.session_datetime))?.length > 0) {
      setFutureSessionsCardSize("small")
    }

  }, [sessionsStates]);


  const nextSessions = useMemo(() => {
    if (!sessions)
      return []

    return sessions?.filter((session) => {
      let durationInMinutes = session?.duration_type === "hour" ? convertFloatHourToMinutes(session?.duration) : session?.duration
      return !sessionExpiredWithMargin(session?.session_datetime, durationInMinutes)
    })
  }, [sessions])

  const oldSessions = useMemo(() => {
    if (!sessions)
      return []

    const expiredSessions = sessions?.filter((session) => {
      let durationInMinutes = session?.duration_type === "hour" ? convertFloatHourToMinutes(session?.duration) : session?.duration
      return sessionExpiredWithMargin(session?.session_datetime, durationInMinutes)
    })

    expiredSessions.sort((a, b) => new Date(a?.session_datetime).getTime() - new Date(b?.session_datetime).getTime())
    expiredSessions.reverse()

    return expiredSessions;
  }, [sessions])


  const handleBulkDelete = () => {
    if (selectedSessions.length > 0) {
      dispatch(resetDeleteBulkSession()); // Reset the bulk delete state before opening modal
      dispatch(showModal("modal-bulk-delete-sessions"));
    }
  };

  // Limpiar selección cuando cambian las sesiones
  useEffect(() => {
    setSelectedSessions([]);
  }, [sessions]);

  // Mostrar modal de eliminación individual cuando se selecciona una sesión
  useEffect(() => {
    if (sessionToDelete) {
      dispatch(resetDeleteBulkSession()); // Reset the bulk delete state before opening modal
      dispatch(showModal("modal-single-bulk-delete-session"));
    }
  }, [sessionToDelete]);

  const hasPastSessions = useMemo(() => {
    // Si no hay sesiones y no está cargando, no hay encuentros pasados
    if (!sessions && !sessionsStates.loading) {
      return false
    }

    // Si hay un error y no está cargando, no hay encuentros pasados
    if (sessionsStates.error && !sessionsStates.loading) {
      return false
    }

    // Si hay encuentros pasados (oldSessions tiene elementos)
    if (oldSessions && oldSessions.length > 0) {
      return true
    }

    // Si está cargando, asumimos que podría haber encuentros pasados
    if (sessionsStates.loading) {
      return true
    }

    // Por defecto, no hay encuentros pasados
    return false
  }, [sessions, sessionsStates.loading, sessionsStates.error, oldSessions])

  // Funciones para manejar la selección de sesiones
  const handleSessionSelect = (sessionId: number, checked: boolean) => {
    console.log('handleSessionSelect called:', { sessionId, checked, currentSelected: selectedSessions });
    if (checked) {
      setSelectedSessions(prev => {
        const newSelected = [...prev, sessionId];
        console.log('Adding session:', { prev, newSelected });
        return newSelected;
      });
    } else {
      setSelectedSessions(prev => {
        const newSelected = prev.filter(id => id !== sessionId);
        console.log('Removing session:', { prev, newSelected });
        return newSelected;
      });
    }
  };

  // Limpiar selección cuando cambian las sesiones
  useEffect(() => {
    setSelectedSessions([]);
  }, [sessions]);

  // Mostrar modal de eliminación individual cuando se selecciona una sesión
  useEffect(() => {
    if (sessionToDelete) {
      dispatch(resetDeleteBulkSession()); // Reset the bulk delete state before opening modal
      dispatch(showModal("modal-single-bulk-delete-session"));
    }
  }, [sessionToDelete]);

  // Limpiar sessionToDelete cuando se cierra el modal
  const handleModalClose = () => {
    setSessionToDelete(null);
  };

  useEffect(() => {
    setLayout({
      ...layout,
      page: {
        title: group?.group_name,
        description: group?.group_name,
        back: `/dashboard/${group?.group_type === 'free_experience' ? 'community/events' : group?.group_type}/${group?.id}`,
      },
      dashboard: {
        tabs: {
          visible: false,
        },
      },
      header: {
        visible: true,
        actions: !isMobile ? [
          <Dropdown key='actions-dropdown'>
            <RrdLink
              to={`/group/${group?.slug}/sessions`}
              key='go-to-preview-btn'
              target="_blank"
            >
              Vista previa
            </RrdLink>
            <button
              disabled={group?.status === 'draft'}
              onClick={() => {
                const link = `${process.env.REACT_APP_SITE_BASE_URL}/group/${group?.slug}`;
                navigator.clipboard.writeText(link);
                dispatch(showModal("modal-share-group-link"));
              }}
              key={'share-experience-btn'}
            >
              Compartir {group?.group_type === "experience" ? "taller" : "membresía"}
            </button>
          </Dropdown>,
          <LinkUi
            disabled={groupStates.loading}
            loading={groupStates.loading}
            key='create-session-btn'
            options={{
              type: "filled",
              size: "lg",
              skin: "purple",
              native: true,
            }}
            href={`/dashboard/${group?.group_type}/${group?.id}/sessions/new`}
          >
            Crear encuentro
          </LinkUi>
        ] : [],
      },
      show_whatsapp: !isMobile
    });
  }, [group, groupStates, isMobile]);

  return (
    <>
      {children}

      <div style={{ height: 36 }} />

      <Grid.Container>
        <Grid.Row>
          <Grid.Col md={6}>
            <CallToAction
              title="Crear nuevo encuentro"
              description={`Solo podrán acceder quienes compren tu ${group?.group_type === "experience" ? "taller" : "membresía"}.`}
              link={`/dashboard/${group?.group_type}/${group?.id}/sessions/new`}
              options={{
                preffix: sessionImage,
                color: "rgba(219, 243, 244, 1)",
              }}
            />
          </Grid.Col>
        </Grid.Row>

        <div style={{ height: 36 }} />

        <Grid.Row>
          <Grid.Col>
            <SectionTitle>Próximos encuentros</SectionTitle>
          </Grid.Col>
        </Grid.Row>

        <div style={{ height: 16 }} />

        <Grid.Row>
          <Grid.Col>
            <CardsGrid size="small">
              {sessionsStates.loading && (
                <>
                  <SessionCardSkeleton />
                  <SessionCardSkeleton />
                  <SessionCardSkeleton />
                </>
              )}

              {(!sessions && !sessionsStates.loading) ||
                ((nextSessions?.length <= 0 ||
                  sessionsStates.error) &&
                  !sessionsStates.loading) ? (
                <EmptyCard
                  title="No tienes encuentros creados"
                  text="Calendariza tus próximos encuentros en vivo para que tu comunidad se entere"
                  icon={videoRecorder}

                // link={{
                //   label: "Crear Nuevo Encuentro",
                //   url: `/dashboard/${group?.group_type}/${group?.id}/sessions/new`,
                // }}
                />
              ) : (
                <>
                  {nextSessions?.map((session) => (
                    <motion.div key={`session-${session.id}`} layout>
                      <ReducedSessionCard
                        key={`session-${session.id}`}
                        session={session}
                        link={`/group/${group?.id}/s/${session.id}`}

                      />
                    </motion.div>
                  ))}
                </>
              )}
            </CardsGrid>
          </Grid.Col>
        </Grid.Row>

        <div style={{ height: 32 }} />

        <Grid.Row>
          <Grid.Col>
            <div style={{ height: "36px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Name>Encuentros pasados</Name>
              {oldSessions.length > 0 && selectedSessions.length > 0 && (
                <DeleteButton
                  onClick={handleBulkDelete}
                >
                  <Icon icon={trashIcon} size="24px" />
                  Eliminar ({selectedSessions.length})
                </DeleteButton>
              )}
            </div>
            <Text>Seleccioná uno o más encuentros con el checkbox de la tarjeta y realizá acciones masivas</Text>
          </Grid.Col>
        </Grid.Row>

        <div style={{ height: 16 }} />

        <Grid.Row>
          <Grid.Col>
            <CardsGrid size={!hasPastSessions ? "small" : "large"}>
              {!hasPastSessions ? (
                <EmptyCard
                  title="No tienes encuentros pasados"
                  text="Sube la grabación adjuntando un link de Zoom, Youtube, Vimeo, Drive, etc."
                  icon={videoRecorder}
                // link={{
                //   label: "Subir un encuentro grabado",
                //   url: `/dashboard/${group?.group_type}/${group?.id}/sessions/new`,
                // }}
                />
              ) : (
                <>
                  {sessionsStates.loading && (
                    <>
                      <SessionCardSkeleton />
                      <SessionCardSkeleton />
                      <SessionCardSkeleton />
                    </>
                  )}

                  {oldSessions?.map((session) => (
                    <SessionCard
                      key={`session-${session.id}`}
                      groupInformation={group}
                      session={session}
                      link={`/group/${group?.id}/s/${session.id}`}
                      showActions
                      showCheckbox={true}
                      isSelected={selectedSessions.includes(session.id)}
                      onSelectionChange={handleSessionSelect}
                      setSessionToDelete={setSessionToDelete}
                    />
                  ))}
                </>
              )}
            </CardsGrid>
          </Grid.Col>
        </Grid.Row>
      </Grid.Container>

      {isMobile && (
        <FloatingMenu />
      )}

      <ModalShareLink />

      <BulkDeleteSessionsModal selectedSessions={selectedSessions} />

      {sessionToDelete && (
        <SingleDeleteSessionModal
          sessionToDelete={sessionToDelete}
          onClose={handleModalClose}
        />
      )}
    </>
  );
};

const state = ({ groupStore, sessionStore }) => {
  const { data: group, states: groupStates } = groupStore.group;
  const { data: sessions, states: sessionsStates } = sessionStore.all;

  return {
    group,
    groupStates,
    sessions,
    sessionsStates,
  };
};

export default connect(state)(Component);
